export const formatHours = (decimalHours) => {
  const isNegative = decimalHours < 0;
  const absHours = Math.abs(decimalHours);
  const hours = Math.floor(absHours);
  const minutes = Math.round((absHours - hours) * 60);
  const sign = isNegative ? '-' : '';
  return `${sign}${hours}:${minutes.toString().padStart(2, '0')}h`;
};

// Transform Zoho Projects timelogs to calendar events
export function transformTimelogsToEvents(apiData) {
    if (!apiData || !apiData.timelogs || !apiData.timelogs.date) return [];
    if (!apiData.timelogs.date.length) return [];

    const events = apiData.timelogs.date.flatMap((day) => {

      const logs = day.tasklogs || day.generallogs || [];
      const date = new Date(day.date_long);

      return logs.map((log) => {
        let start, end;

        if (log.start_time && log.end_time) {
          const startTime = log.start_time.split(':');
          const endTime = log.end_time.split(':');
          start = new Date(date);
          start.setHours(startTime[0], startTime[1]);
          end = new Date(date);
          end.setHours(endTime[0], endTime[1]);
          if (end <= start) {
            end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
          }
          const durationHours = (end - start) / (1000 * 60 * 60);
          if (durationHours > 23) {
            end = new Date(start.getTime() + 23 * 60 * 60 * 1000);
          }
        } else {
          start = new Date(date);
          start.setHours(4, 0);
          const durationMinutes = parseInt(log.total_minutes) || 0;
          if (durationMinutes > 23 * 60) {
            end = new Date(start.getTime() + 23 * 60 * 60 * 1000);
          } else {
            end = new Date(start.getTime() + durationMinutes * 60000);
          }
        }

        const displayTitle = (log.notes || log.name || log.log_name || '');

        const event = {
          title: displayTitle,
          start,
          end,
          projectId: log.project?.id_string || '',
          projectName: log.project?.name || '',
          approval_status: log.approval_status || '',
          billingtype: log.bill_status || '',
          logId: `projects:${log.id_string || log.id || ''}`,
          taskId: log.task?.id_string || log.task?.id || '',
          taskName: log.task?.name || '',
          isProjectArchived: !!log.isProjectArchived,
        };

        return event;
      });
    });

    return events;
  }

// Transform a Zoho People timelog to a calendar event
export function handlePeopleTimelogToEvent(timelogDetails) {
    // Handle both camelCase variants: timelogId (gettimelogs) and timeLogId (gettimelogdetails)
    const id = timelogDetails.timelogId || timelogDetails.timeLogId || timelogDetails.timelog_id;
    if (!id) return null;

    const workDate = timelogDetails.workDate || timelogDetails.work_date;
    if (!workDate) return null;
    const date = new Date(workDate);

    // Prefer explicit time fields; be resilient to API variants
    const getHM = (val) => {
      if (!val) return null;
      // Accept "HH:mm", "HH:mm:ss", or "HH:mm AM/PM"
      const ampm = /am|pm/i.test(val);
      let [h, m] = val.replace(/\s?(AM|PM)$/i, '').split(':');
      let hour = parseInt(h, 10);
      const min = parseInt(m || '0', 10);
      if (ampm) {
        const isPM = /pm/i.test(val);
        if (isPM && hour < 12) hour += 12;
        if (!isPM && hour === 12) hour = 0;
      }
      return { hour, min };
    };

    const fromRaw = timelogDetails.fromTimeInTimeFormat || timelogDetails.fromTime || timelogDetails.fromTimeInHrs;
    const toRaw   = timelogDetails.toTimeInTimeFormat   || timelogDetails.toTime   || timelogDetails.toTimeInHrs;

    let start = new Date(date);
    let end = new Date(date);

    const fromHM = getHM(fromRaw);
    const toHM = getHM(toRaw);

    if (fromHM && toHM) {
      start.setHours(fromHM.hour, fromHM.min, 0, 0);
      end.setHours(toHM.hour, toHM.min, 0, 0);
    } else {
      // Fallback using duration
      const mins = timelogDetails.hoursInMins
        ?? (typeof timelogDetails.hours === 'number' ? Math.round(timelogDetails.hours * 60) : 60);
      start.setHours(4, 0, 0, 0);
      end = new Date(start.getTime() + mins * 60000);
    }

    return {
      title: timelogDetails.description || timelogDetails.jobName || '',
      start,
      end,
      projectId: '',
      timecodeId: timelogDetails.jobId || '',
      projectName: '',
      timecodeName: timelogDetails.jobName || '',
      approval_status: timelogDetails.approvalStatus || '',
      billingtype: timelogDetails.billingStatus === 'billable' ? 'Billable' : 'Non Billable',
      logId: `people:${id}`,
      isPeople: true,
      employeeEmail: timelogDetails.employeeMailId || '',
    };
  }

// Transform a Zoho People v3 leave request into one calendar event per leave day.
// Each day event spans 00:00–23:59 so it renders as a full-height column block,
// identical in shape to a regular timelog. Uses the `days` map from the API
// response to skip days with zero leave count (e.g. half-day sessions).
export function transformLeaveToEvent(leave) {
  const id = leave.leave_id;
  if (!id) return [];

  const parseZohoDate = (s) => {
    if (!s) return null;
    const months = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 };
    const [d, mon, y] = s.split('-');
    return new Date(+y, months[mon], +d);
  };

  const rawStatus = (leave.approval_status || '').toUpperCase();
  if (rawStatus === 'CANCELLED' || rawStatus === 'CANCELED') return [];
  const leaveStatus = rawStatus === 'APPROVED' ? 'Approved' : rawStatus === 'REJECTED' ? 'Rejected' : 'Pending';
  const leaveTypeName = leave.leave_type?.name || '';

  // Collect days with actual leave (leave_count > 0)
  const leaveDays = leave.days || {};
  let activeDates = Object.entries(leaveDays)
    .filter(([, v]) => parseFloat(v.leave_count || '0') > 0)
    .map(([dateStr]) => parseZohoDate(dateStr))
    .filter(Boolean)
    .sort((a, b) => a - b);

  // Fall back to from_date–to_date range if days map is absent or all zero
  if (activeDates.length === 0) {
    const from = parseZohoDate(leave.from_date);
    const to   = parseZohoDate(leave.to_date);
    if (!from || !to) return [];
    const cursor = new Date(from);
    while (cursor <= to) {
      activeDates.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  return activeDates.map(date => {
    const start = new Date(date); start.setHours(0, 0, 0, 0);
    const end   = new Date(date); end.setHours(23, 59, 0, 0);
    return {
      title: leaveTypeName || 'Fravær',
      start,
      end,
      logId: `leave:${id}:${date.toISOString().split('T')[0]}`,
      isPeopleLeave: true,
      leaveStatus,
      leaveTypeName,
      employeeZohoId: leave.employee?.zoho_id || null,
      employeeName: leave.employee?.name || '',
      notes: leave.Reason || leave.reason || '',
    };
  });
}